/** Demo Mode only - shared trend chart wrapper (actual vs budget vs forecast/prior year)
 * used by Overview, Forecast and EBIT Analysis. Wraps @mui/x-charts (already an installed
 * dependency - no new chart library introduced). Distinguishes completed (actual) periods
 * from forecast periods via a vertical marker rather than colour alone. */

import { LineChart } from '@mui/x-charts/LineChart';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { PERIOD_LABELS, CURRENT_PERIOD_INDEX } from '../../../mocks/data/financeData';

export interface TrendSeriesConfig {
  label: string;
  data: number[];
  color: string;
  dashed?: boolean;
}

export interface TrendChartProps {
  title: string;
  series: TrendSeriesConfig[];
  height?: number;
  valueFormatter?: (value: number | null) => string;
}

const TrendChart = ({
  title, series, height = 280, valueFormatter,
}: TrendChartProps) => {
  const theme = useTheme();
  const { t } = useTranslation('common');

  return (
    <Box>
      <Typography variant="textMD"
        fontWeight={600}
        sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Typography variant="textXS"
        color="text.secondary"
        sx={{ mb: 2, display: 'block' }}>
        {t('financeTrendChartActualsForecastHint', 'Solid markers up to {{period}} are actuals; periods after that are forecast.', { period: PERIOD_LABELS[CURRENT_PERIOD_INDEX] })}
      </Typography>
      <LineChart
        height={height}
        xAxis={[{ scaleType: 'point', data: PERIOD_LABELS }]}
        series={series.map((s) => ({
          label: s.label,
          data: s.data,
          color: s.color,
          curve: 'linear',
          valueFormatter,
          showMark: true,
        }))}
        slotProps={{ legend: { direction: 'row', position: { vertical: 'top', horizontal: 'right' }, labelStyle: { fontSize: 11 } } }}
        sx={{
          '.MuiLineElement-root': { strokeWidth: 2 },
          '& .MuiChartsAxis-tickLabel': { fontSize: 11, fill: theme.palette.custom.gray[600] },
        }}
      />
    </Box>
  );
};

export default TrendChart;
